import type {Equipment} from './_models'

export type EquipmentOption = {
  value: number
  label: string
}

export type EquipmentLoadStatus = 'idle' | 'loading' | 'loaded' | 'empty' | 'error' | 'forbidden'

export type EquipmentFetchResult = {
  options: EquipmentOption[]
  status: Exclude<EquipmentLoadStatus, 'idle' | 'loading'>
  message?: string
}

export const normalizeEquipmentRows = (response: unknown): Record<string, unknown>[] => {
  const payload = (response as {data?: unknown})?.data ?? response

  if (Array.isArray(payload)) {
    return payload.filter((row) => row && typeof row === 'object') as Record<string, unknown>[]
  }
  if (payload && typeof payload === 'object') {
    const obj = payload as Record<string, unknown>
    for (const key of ['content', 'data', 'items'] as const) {
      const nested = obj[key]
      if (Array.isArray(nested)) {
        return nested.filter((row) => row && typeof row === 'object') as Record<string, unknown>[]
      }
    }
  }

  return []
}

export const isRecognizedEquipmentPayload = (response: unknown): boolean => {
  if (response == null) return true
  if (normalizeEquipmentRows(response).length > 0) return true
  if (Array.isArray(response)) return true

  const payload = (response as {data?: unknown})?.data ?? response
  if (Array.isArray(payload)) return true
  if (payload && typeof payload === 'object') {
    const obj = payload as Record<string, unknown>
    return ['content', 'data', 'items'].some((key) => Array.isArray(obj[key]))
  }
  return false
}

export const toEquipmentRecord = (row: unknown): Equipment | null => {
  if (!row || typeof row !== 'object') return null

  const record = row as Record<string, unknown>
  const id = Number(record.id ?? record.equipmentId)
  const name = String(record.name ?? record.equipmentName ?? '').trim()
  if (!Number.isFinite(id) || id <= 0 || !name) return null

  const codeRaw = record.code ?? record.equipmentCode
  const code = codeRaw != null && String(codeRaw).trim() ? String(codeRaw).trim() : undefined

  return {
    id,
    name,
    code,
    vesselId: record.vesselId != null ? Number(record.vesselId) : null,
    vesselName: record.vesselName != null ? String(record.vesselName) : null,
    companyId: record.companyId != null ? Number(record.companyId) : null,
    companyName: record.companyName != null ? String(record.companyName) : null,
  }
}

export const normalizeEquipmentRecords = (response: unknown): Equipment[] => {
  return normalizeEquipmentRows(response)
    .map(toEquipmentRecord)
    .filter((row): row is Equipment => row != null)
}

export const toEquipmentOptions = (records: Equipment[]): EquipmentOption[] =>
  records.map((equipment) => ({
    value: equipment.id,
    label: equipment.code ? `${equipment.name} (${equipment.code})` : equipment.name,
  }))

export const resolveEquipmentFetchError = (
  error: unknown
): {status: 'error' | 'forbidden'; message: string} => {
  const status = (error as {response?: {status?: number; data?: {message?: string}}})?.response
    ?.status
  if (status === 403) {
    return {status: 'forbidden', message: 'Access denied loading equipment.'}
  }
  const message =
    (error as {response?: {data?: {message?: string}}})?.response?.data?.message ||
    (error as {message?: string})?.message ||
    'Unable to load equipment.'
  return {status: 'error', message}
}

export async function fetchEquipmentOptionsForVessel(
  vesselId: number,
  loader: (id: number) => Promise<unknown>
): Promise<EquipmentFetchResult> {
  try {
    const raw = await loader(vesselId)
    const records = normalizeEquipmentRecords(raw)

    if (records.length === 0) {
      if (!isRecognizedEquipmentPayload(raw)) {
        console.warn('[equipment] unexpected response shape', raw)
        return {
          options: [],
          status: 'error',
          message: 'Unexpected equipment response shape.',
        }
      }
      return {
        options: [],
        status: 'empty',
        message: 'No equipment found for selected vessel.',
      }
    }

    return {
      options: toEquipmentOptions(records),
      status: 'loaded',
    }
  } catch (error) {
    const resolved = resolveEquipmentFetchError(error)
    return {
      options: [],
      status: resolved.status,
      message: resolved.message,
    }
  }
}
